import { TemplateResult, html, CSSResultGroup } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { colSpanRange } from '../../base/outline-grid/outline-column/outline-column';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { Size } from '../../base/outline-element/utils/types';
import componentStyles from './idb-card-grid.css.lit';

import '../../base/outline-container/outline-container';
import '../../base/outline-grid/outline-grid';
import '../../base/outline-grid/outline-column/outline-column';
import { HorizontalAlignment } from '../../base/outline-element/utils/types';
import { SlotController } from '../../controllers/slot-controller';
import { rowBackgroundPropertyData } from '../../../data';
import { classMap } from 'lit/directives/class-map.js';

export type rowBackgrounds = typeof rowBackgroundPropertyData;

/**
 * The IDB Card Grid component.
 * @element idb-card-grid
 * @property columns-lg — Set the columns for large screens
 * @property columns-md— Set the columns for medium screens
 * @property columns-sm— Set the columns for small screens
 * @property columns-default — Set the columns for default mode
 * @property gap-size — Set the gap size between columns
 * @property full-bleed — Sets the outline container to fill the space on the page
 * @property container-align — Sets the alignment of the container
 * @property background-color — Sets the background color of the card row
 * @todo add is nested back in. It was taken out because the CSS on the Outline-Container was overridden by other CSS.
 * Overall there is no padding on the elements. So is nested may not be needed.
 */

@customElement('idb-card-grid')
export class IdbCardGrid extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    false // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  /**
   * How many columns to span on large screens 1 | 2 | 3 | 4
   */
  @property({ type: Number, attribute: 'columns-lg' })
  columnsLg: 1 | 2 | 3 | 4 | 6 = 4;

  /**
   * How many columns to span on medium screens 1 | 2 | 3 | 4
   */
  @property({ type: Number, attribute: 'columns-md' })
  columnsMd: 1 | 2 | 3 | 4 | 6 = 3;

  /**
   * How many columns to span on small screens 1 | 2 | 3 | 4
   */
  @property({ type: Number, attribute: 'columns-sm' })
  columnsSm: 1 | 2 | 3 | 4 | 6 = 2;

  /**
   * How many columns to default to 1 | 2 | 3 | 4
   */
  @property({ type: Number, attribute: 'columns-default' })
  columnsDefault: 1 | 2 | 3 | 6 = 2;

  /**
   * Gap size between cards.
   */
  @property({ type: String, attribute: 'gap-size' })
  gapSize: Size = 'small';

  /**
   * Whether or not this is a full bleed container.
   */
  @property({ type: Boolean, reflect: true, attribute: 'full-bleed' })
  fullBleed = false;

  /**
   * The color class to apply to the full card.
   */
  @property({ attribute: 'row-background-color' })
  rowBackgroundColor: rowBackgrounds;

  /**
   * The horizontal position of the container.
   */
  @property({ type: String, reflect: true, attribute: 'container-align' })
  containerAlign: HorizontalAlignment = 'center';

  @property({ type: String, attribute: 'heading' })
  heading = '';

  @property({ type: String, attribute: 'sub-heading' })
  subHeading = '';

  @property({ type: String, attribute: 'heading-align' })
  headingAlign: HorizontalAlignment = 'center';

  observer: MutationObserver;

  observerMethod = () => {
    this.setSlots();
  };

  connectedCallback() {
    super.connectedCallback();
    this.observer = new MutationObserver(this.observerMethod);
    this.observer.observe(this, { childList: true });
    this.setSlots();
  }

  firstUpdated() {}

  disconnectedCallback() {
    this.observer.disconnect();
  }

  render(): TemplateResult {
    const headingClasses = {
      'heading-container': true,
      'margin-bottom': this.subHeading,
    };

    return html`
      ${
        this.heading
          ? html`
        <div class="${classMap(headingClasses)}">
          <idb-heading level="h2" level-size="4xl" level-style="semibold" capital-style="capitalize" class="text-center">${
            this.heading
          }</idb-heading>
          <idb-heading level="h3" level-size="3xl" level-style="semibold" capital-style="none" class="text-center" margin-size="0px">${
            this.subHeading
          }</idb-heading>
        </div>
      `
          : null
      }
      <div class="grid-container">
        <outline-grid gap-size="${this.gapSize}" ?full-bleed=${
      this.fullBleed
    } container-align="${this.containerAlign}">
            ${this.columnTemplate()}
        </outline-grid>
      </div>
    `;
  }

  columnTemplate = (): TemplateResult[] => {
    const spanLg = this.calculateColumns(this.columnsLg);
    const spanMd = this.calculateColumns(this.columnsMd);
    const spanSm = this.calculateColumns(this.columnsSm);
    const spanDefault = this.calculateColumns(this.columnsDefault);

    let counter = 1;
    const columnArray = [];
    while (counter <= this.childElementCount) {
      columnArray.push(
        html`
          <outline-column
            col-span-default=${ifDefined(spanDefault)}
            col-span-sm=${ifDefined(spanSm)}
            col-span-md=${ifDefined(spanMd)}
            col-span-lg=${ifDefined(spanLg)}
            content-align="top"
          >
            <slot name="grid-module-${counter}"></slot>
          </outline-column>
        `
      );

      counter++;
    }
    return columnArray;
  };

  setSlots = () => {
    let counter = 0;
    while (counter <= this.childElementCount) {
      if (this.children[counter]) {
        this.children[counter].setAttribute(
          'slot',
          `grid-module-${counter + 1}`
        );
      }
      counter++;
    }
    this.requestUpdate();
  };

  calculateColumns = (columns: number) => {
    let col: colSpanRange | undefined;
    switch (columns) {
      case 1:
        col = 12;
        break;
      case 2:
        col = 6;
        break;
      case 3:
        col = 4;
        break;
      case 4:
        col = 3;
        break;
      case 6:
        col = 2;
        break;
      default:
        break;
    }
    return col;
  };
}
